import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  Inject,
  LineSeries,
  DateTime,
  StripLine,
  Tooltip as ChartTooltip,
} from '@syncfusion/ej2-react-charts';

const Card = ({ children, sx = {} }) => (
  <Box
    sx={{
      bgcolor: 'background.paper',
      border: '0.5px solid',
      borderColor: 'divider',
      borderRadius: 2,
      p: 2,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const SecLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: '10px',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: 'text.secondary',
      mb: 0.5,
    }}
  >
    {children}
  </Typography>
);

const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;
const WEEK_INTERVAL_STEPS = [1, 2, 4, 8, 12, 26];

const getXAxisWeekInterval = (data) => {
  const times = (data || [])
    .map((d) => new Date(d.x).getTime())
    .filter((t) => !isNaN(t));
  if (times.length < 2) return 2;

  const spanWeeks = (Math.max(...times) - Math.min(...times)) / MS_PER_WEEK;
  const target = Math.ceil(spanWeeks / 14);
  return WEEK_INTERVAL_STEPS.find((step) => step >= target) ?? 26;
};

const KpiTrendChart = ({ title, weeks, valueKey, goodDirection, subtitle }) => {
  // Format data: filter out weeks where value is null or earned is zero
  const chartData = (weeks || [])
    .map((w) => ({
      x: new Date(w.weekEnding + 'T00:00:00Z'), // parse safely in UTC
      y: w[valueKey],
      isLocked: w.isLocked,
    }))
    .filter((d) => d.y != null);

  const values = chartData.map((d) => d.y);
  const minValue = values.length > 0 ? Math.min(...values) : 1.0;
  const maxValue = values.length > 0 ? Math.max(...values) : 1.0;

  const yMin = Math.max(0, Math.min(0.5, minValue - 0.1));
  const yMax = Math.max(1.5, maxValue + 0.1);

  const xAxisWeekInterval = getXAxisWeekInterval(chartData);

  // Cool teal (good) vs dusty rose (bad) — distinguishable without gauge-level
  // saturation. Which side of 1.0 is good follows goodDirection (ticket #264).
  const GOOD_BAND = '#A8DAD5';
  const BAD_BAND = '#E0B0B0';
  const aboveIsGood = goodDirection === 'above';

  const stripLines = [
    {
      // Band above 1.0 — bad unless above is good
      start: 1.0,
      end: 999,
      color: aboveIsGood ? GOOD_BAND : BAD_BAND,
      opacity: 0.45,
      zIndex: 'Behind',
    },
    {
      // Band below 1.0 — good unless above is good
      start: 0,
      end: 1.0,
      color: aboveIsGood ? BAD_BAND : GOOD_BAND,
      opacity: 0.45,
      zIndex: 'Behind',
    },
    {
      // Reference line at 1.0
      start: 1.0,
      end: 1.0,
      color: '#666666',
      size: 1.5,
      sizeType: 'Pixel',
      dashArray: '4,4',
      zIndex: 'Over',
    },
  ];

  return (
    <Card>
      <SecLabel>{title}</SecLabel>
      {subtitle && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 1.5 }}
        >
          {subtitle}
        </Typography>
      )}

      <ChartComponent
        height="320px"
        primaryXAxis={{
          valueType: 'DateTime',
          labelFormat: 'MMM d',
          intervalType: 'Weeks',
          interval: xAxisWeekInterval,
          rangePadding: 'Round',
          edgeLabelPlacement: 'Shift',
          majorGridLines: { width: 0 },
          labelStyle: { size: '9px' },
          labelIntersectAction: 'Rotate45',
        }}
        primaryYAxis={{
          minimum: yMin,
          maximum: yMax,
          majorGridLines: { color: 'rgba(136,135,128,.12)' },
          labelStyle: { size: '9px' },
          labelFormat: '{value}',
          stripLines: stripLines,
        }}
        axisLabelRender={(args) => {
          if (args.axis.name === 'primaryXAxis') {
            const d = new Date(args.value);
            if (d.getMonth() === 0) {
              args.text = `Jan '${String(d.getFullYear()).slice(-2)}`;
            }
          }
        }}
        tooltipRender={(args) => {
          if (args.point && args.point.x) {
            const d = new Date(args.point.x);
            const formattedDate = d.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              timeZone: 'UTC',
            });
            const yVal =
              args.point.y !== null && args.point.y !== undefined
                ? args.point.y.toFixed(3)
                : '-';
            args.text = `Week ending: ${formattedDate}\nValue: ${yVal}`;
          }
        }}
        tooltip={{
          enable: true,
          fadeOutDuration: 200,
        }}
        legendSettings={{ visible: false }}
        chartArea={{ border: { width: 0 } }}
      >
        <Inject services={[LineSeries, DateTime, StripLine, ChartTooltip]} />
        <SeriesCollectionDirective>
          <SeriesDirective
            dataSource={chartData}
            xName="x"
            yName="y"
            name={valueKey === 'ptdLpf' ? 'LPF' : 'SPI'}
            type="Line"
            fill={valueKey === 'ptdLpf' ? '#1D9E75' : '#378ADD'}
            width={2}
            marker={{
              visible: true,
              height: 6,
              width: 6,
              shape: 'Circle',
            }}
          />
        </SeriesCollectionDirective>
      </ChartComponent>
    </Card>
  );
};

export default KpiTrendChart;
